import { useCallback, useEffect, useRef, useState } from 'react';
import PipelineAPI from '../api/pipeline';
import { ReviewTrace } from '../api/structs';

export interface ReviewTracesState {
  traces: ReviewTrace[];
  loading: boolean;
  /** Empty while healthy; the reason the fetch failed otherwise. */
  error: string;
  /** "store" = full system prompts; "run" = the capped heads off the live run. */
  source: 'store' | 'run' | '';
  /** Re-reads the run's traces (after a correction, say). */
  refetch: () => void;
}

const messageOf = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'Could not load the reviewer trace.';
};

/**
 * The raw reviewer call log for one run, fetched lazily: nothing goes out
 * until `enabled` (a verdict panel actually opened) and a run id are both
 * true, and a run is fetched only ONCE however often the panel is reopened —
 * traces are append-only history, so re-reading them on every toggle would be
 * a request per click for an answer that did not change. `refetch()` is the
 * deliberate re-read after something (a correction) changed the record.
 *
 * A response that lost the race — the run id changed, or the caller unmounted,
 * while it was in flight — is dropped rather than written over newer state.
 */
export function useReviewTraces(runId: string | undefined, enabled: boolean): ReviewTracesState {
  const [traces, setTraces] = useState<ReviewTrace[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState<'store' | 'run' | ''>('');
  const [version, setVersion] = useState(0);
  /** The `runId::version` already fetched, so re-enabling does not refetch. */
  const fetchedRef = useRef('');

  useEffect(() => {
    if (!runId || !enabled) return undefined;
    const key = `${runId}::${version}`;
    if (fetchedRef.current === key) return undefined;
    fetchedRef.current = key;

    let cancelled = false;
    let settled = false;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const data = await PipelineAPI.getReviewTraces(runId);
        settled = true;
        if (cancelled) return;
        setTraces(data?.traces || []);
        setSource(data?.source || '');
      } catch (err) {
        settled = true;
        if (cancelled) return;
        setTraces([]);
        setSource('');
        setError(messageOf(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
      // React 18 StrictMode runs every effect twice in development: mount,
      // immediate cleanup, mount again. Without releasing the key here the
      // second run would see it already claimed, skip the fetch, and leave the
      // first (now cancelled) response as the only one — `loading` would stay
      // true forever. Releasing it only when the request had NOT settled keeps
      // the once-per-key guarantee for every real re-render.
      if (!settled) fetchedRef.current = '';
    };
  }, [runId, enabled, version]);

  const refetch = useCallback(() => setVersion((current) => current + 1), []);

  return { traces, loading, error, source, refetch };
}

export default useReviewTraces;
